import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { BOOKING_BACKEND_URL } from '../services/api';

// 客戶資料介面
export interface Customer {
  id: string;
  email: string;
  name: string;
  phone?: string;
  avatar?: string;
  authEnabled: boolean;
  createdAt: string;
  updatedAt?: string;
}

// 客戶認證 Context 類型
interface CustomerAuthContextType {
  customer: Customer | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; name: string; password: string; phone: string }) => Promise<void>;
  logout: () => void;
  refreshCustomer: () => Promise<void>;
  updateCustomer: (updatedData: Partial<Customer>) => void;
}

export const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

interface CustomerAuthProviderProps {
  children: ReactNode;
}

const TOKEN_KEY = 'customer_auth_token';

export const CustomerAuthProvider: React.FC<CustomerAuthProviderProps> = ({ children }) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // 從 localStorage 載入 token
  useEffect(() => {
    const savedToken = localStorage.getItem(TOKEN_KEY);
    if (savedToken) {
      setToken(savedToken);
    }
  }, []);

  // 當有 token 時自動載入客戶資料
  useEffect(() => {
    if (token) {
      refreshCustomer();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  // 重新載入客戶資料
  const refreshCustomer = useCallback(async () => {
    if (!token) {
      setCustomer(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${BOOKING_BACKEND_URL}/api/customers/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customer profile');
      }

      const data = await response.json();
      setCustomer(data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Failed to refresh customer data:', error);
      // Token 可能已失效，清除它
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setCustomer(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // 登入
  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${BOOKING_BACKEND_URL}/api/customers/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '登入失敗');
      }

      const data = await response.json();
      const { customer: customerData, token: authToken } = data;

      // 儲存 token
      localStorage.setItem(TOKEN_KEY, authToken);
      setToken(authToken);
      setCustomer(customerData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // 註冊
  const register = async (data: { email: string; name: string; password: string; phone: string }) => {
    try {
      const response = await fetch(`${BOOKING_BACKEND_URL}/api/customers/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '註冊失敗');
      }

      const result = await response.json();
      const { customer: customerData, token: authToken } = result;

      // 儲存 token 並設定已登入狀態
      localStorage.setItem(TOKEN_KEY, authToken);
      setToken(authToken);
      setCustomer(customerData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  };

  // 登出
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setCustomer(null);
    setIsAuthenticated(false);
  }, []);

  // 更新客戶資料（本地）
  const updateCustomer = useCallback((updatedData: Partial<Customer>) => {
    setCustomer((prev) => {
      if (!prev) return null;
      return { ...prev, ...updatedData };
    });
  }, []);

  return (
    <CustomerAuthContext.Provider
      value={{
        customer,
        token,
        isAuthenticated,
        isLoading,
        login,
        register,
        logout,
        refreshCustomer,
        updateCustomer,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

// Custom hook for using the customer auth context
export const useCustomerAuth = () => {
  const context = React.useContext(CustomerAuthContext);
  if (context === undefined) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};
